import { useState, useEffect } from 'react';
import { useOrganization } from './OrganizationProvider';
import { useCurrentUser } from './ClerkProviderWrapper';
import { Users, Mail, Shield, UserPlus, Trash2, Clock, Building2, AlertCircle, CheckCircle, CreditCard, Key, Search, ChevronLeft, ChevronRight, ExternalLink, Copy, Plus, Eye, EyeOff, AlertTriangle, LayoutDashboard, Edit3, Save, X, Activity, Download } from 'lucide-react';
import api, { getMembers, removeMember, updateMemberRole, createStripeCheckout, createStripePortal, getSubscriptionInfo, getApiKeys, createApiKey, revokeApiKey, getMyUsage, getAuditLogs, updateOrganization, downloadOrganizationBackup, importOrganizationBackup, resendInvitation, getInvoices, purgeAuditLogs } from '../api';
import UsageMeters from './UsageMeters';

/**
 * Admin Dashboard for managing organizations and invitations.
 * Styled consistently with the rest of the DriftOps application.
 */
export default function AdminDashboard() {
    const { currentOrg: currentOrganization } = useOrganization();
    const { user } = useCurrentUser();
    const [activeTab, setActiveTab] = useState('overview');
    const [invitations, setInvitations] = useState([]);

    // API Keys state
    const [apiKeys, setApiKeys] = useState([]);
    const [isApiKeysLoading, setIsApiKeysLoading] = useState(false);
    const [showCreateKey, setShowCreateKey] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [createdKey, setCreatedKey] = useState(null);  // raw key shown once
    const [showRawKey, setShowRawKey] = useState(false);
    const [copiedKey, setCopiedKey] = useState(false);
    const [revokeConfirmId, setRevokeConfirmId] = useState(null);
    const [newKeyScopes, setNewKeyScopes] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [usage, setUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);

    // Audit Logs state
    const [auditLogs, setAuditLogs] = useState([]);
    const [isAuditLogsLoading, setIsAuditLogsLoading] = useState(false);

    // Org settings editing
    const [isEditingOrg, setIsEditingOrg] = useState(false);
    const [editOrgName, setEditOrgName] = useState('');
    const [editOrgSlug, setEditOrgSlug] = useState('');
    const [orgSaveLoading, setOrgSaveLoading] = useState(false);
    const [orgSaveError, setOrgSaveError] = useState(null);
    const [orgSaveSuccess, setOrgSaveSuccess] = useState(false);

    // Pagination & Search
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 8;

    // Billing
    const [isBillingLoading, setIsBillingLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);

    // Backup / import state
    const [isBackupLoading, setIsBackupLoading] = useState(false);
    const [backupSuccess, setBackupSuccess] = useState(false);
    const [backupError, setBackupError] = useState(null);

    const [importFile, setImportFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Form state for new invitation
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('viewer');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState(null);

    // Load data
    useEffect(() => {
        if (currentOrganization) {
            loadInvitations();
            loadMembers();
        }
    }, [currentOrganization]);

    // Load API keys when tab is selected
    useEffect(() => {
        if (activeTab === 'apikeys' && currentOrganization) {
            setIsApiKeysLoading(true);
            getApiKeys()
                .then(keys => setApiKeys(keys))
                .catch(err => console.error('Failed to load API keys:', err))
                .finally(() => setIsApiKeysLoading(false));
        }
    }, [activeTab, currentOrganization]);

    // Load usage data when overview tab is selected
    useEffect(() => {
        if (activeTab === 'overview' && currentOrganization) {
            setUsageLoading(true);
            getMyUsage()
                .then(data => setUsage(data))
                .catch(err => console.error('Failed to load usage statistics:', err))
                .finally(() => setUsageLoading(false));
        }
    }, [activeTab, currentOrganization]);

    // Load subscription info and invoices when billing tab is selected
    useEffect(() => {
        if (activeTab === 'billing' && currentOrganization?.stripe_customer_id) {
            getSubscriptionInfo()
                .then(info => setSubscriptionInfo(info))
                .catch(() => setSubscriptionInfo(null));
            setIsInvoicesLoading(true);
            getInvoices()
                .then(data => setInvoices(data.items || []))
                .catch(() => setInvoices([]))
                .finally(() => setIsInvoicesLoading(false));
        } else if (activeTab === 'billing') {
            setSubscriptionInfo(null);
            setInvoices([]);
        }
    }, [activeTab, currentOrganization]);

    // Load audit logs when auditlogs tab is selected
    useEffect(() => {
        if (activeTab === 'auditlogs' && currentOrganization) {
            setIsAuditLogsLoading(true);
            getAuditLogs()
                .then(logs => setAuditLogs(Array.isArray(logs) ? logs : []))
                .catch(err => console.error('Failed to load audit logs:', err))
                .finally(() => setIsAuditLogsLoading(false));
        }
    }, [activeTab, currentOrganization]);

    const loadInvitations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/api/invitations');
            const data = response.data;
            setInvitations(Array.isArray(data) ? data : (data.items || []));
        } catch (err) {
            setError('Failed to load invitations');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadMembers = async () => {
        try {
            const data = await getMembers();
            setMembers(data);
        } catch (err) {
            console.error('Failed to load members:', err);
            // Don't show error to user if it's just members failing (invites might work)
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        setInviteSuccess(null);
        setError(null);

        try {
            await api.post('/api/invitations', {
                email: inviteEmail,
                role: inviteRole
            });
            setInviteSuccess(`Invitation sent to ${inviteEmail}`);
            setInviteEmail('');
            loadInvitations();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send invitation');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleRevoke = async (invitationId) => {
        if (!confirm('Are you sure you want to revoke this invitation?')) return;

        try {
            await api.delete(`/api/invitations/${invitationId}`);
            loadInvitations();
        } catch (err) {
            setError('Failed to revoke invitation');
        }
    };

    const handleResendInvitation = async (invitationId) => {
        try {
            await resendInvitation(invitationId);
            loadInvitations();
            setInviteSuccess('Invitation resent successfully');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to resend invitation');
        }
    };

    const handleRemoveMember = async (userId) => {
        if (!confirm('Are you sure you want to remove this member from the organization?')) return;

        try {
            await removeMember(userId);
            loadMembers(); // Refresh list
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to remove member');
        }
    };

    const handleUpdateOrg = async () => {
        setOrgSaveLoading(true);
        setOrgSaveError(null);
        setOrgSaveSuccess(false);
        try {
            await updateOrganization(editOrgName.trim(), editOrgSlug.trim());
            setOrgSaveSuccess(true);
            setIsEditingOrg(false);
            setTimeout(() => setOrgSaveSuccess(false), 3000);
        } catch (err) {
            setOrgSaveError(err.response?.data?.detail || 'Failed to update organization');
        } finally {
            setOrgSaveLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateMemberRole(userId, newRole);
            loadMembers();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update member role');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getTimeRemaining = (expiresAt) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diff = expires - now;

        if (diff <= 0) return 'Expired';

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) return `${hours}h ${minutes}m remaining`;
        return `${minutes}m remaining`;
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin':
                return 'bg-purple-900/30 text-purple-300';
            case 'editor':
                return 'bg-blue-900/30 text-blue-300';
            default:
                return 'bg-gray-700 text-gray-300';
        }
    };

    // --- Billing Handlers ---
    const TIERS = [
        {
            id: 'free',
            name: 'Free',
            price: 0,
            priceId: null,
            metered: 'No credit card required',
            features: [
                '3 Data Contracts',
                '100 comparisons/mo',
                '1 user',
                'Schema comparator tool',
                'JSON & YAML export',
            ],
        },
        {
            id: 'starter',
            name: 'Starter',
            price: 29,
            priceId: import.meta.env.VITE_STRIPE_PRICE_STARTER || 'price_1T3Ow9DziLin7NugrKjpsGYl',
            metered: '$0.05 / event after 10K',
            features: [
                '25 Data Contracts',
                '10,000 comparisons/mo',
                '5 team members',
                'Drift detection & Slack alerts',
                'S3, Azure & GCP polling',
                '30-day retention',
            ],
        },
        {
            id: 'team',
            name: 'Team',
            price: 79,
            priceId: import.meta.env.VITE_STRIPE_PRICE_TEAM || 'price_1THVJhDziLin7NugyT9Kdw6E',
            metered: '$0.03 / event after 30K',
            features: [
                '75 Data Contracts',
                '30,000 comparisons/mo',
                '15 team members',
                'All Starter features',
                'SFTP & FTP polling',
                '60-day retention',
            ],
        },
        {
            id: 'pro',
            name: 'Pro',
            price: 199,
            priceId: import.meta.env.VITE_STRIPE_PRICE_PRO || 'price_1T3OxmDziLin7NugMZSD0tTo',
            metered: '$0.01 / event after 150K',
            features: [
                '500 Data Contracts',
                '150,000 comparisons/mo',
                '50 team members',
                'AS2 & OFTP2 endpoints',
                'Audit logs',
                '90-day retention',
            ],
        },
    ];

    const handleSelectTier = async (tier) => {
        try {
            setIsBillingLoading(true);
            setError(null);

            const currentTier = currentOrganization?.tier || 'free';

            // Downgrade to Free → open portal to cancel subscription
            if (tier.id === 'free') {
                if (currentOrganization?.stripe_customer_id) {
                    const data = await createStripePortal();
                    if (data.url) window.location.href = data.url;
                }
                return;
            }

            // Already on a paid plan → Stripe Customer Portal handles upgrade/downgrade
            if (currentOrganization?.stripe_customer_id && currentTier !== 'free') {
                const data = await createStripePortal();
                if (data.url) window.location.href = data.url;
            } else {
                // New subscription → Stripe Checkout
                const data = await createStripeCheckout(tier.priceId);
                if (data.url) window.location.href = data.url;
            }
        } catch (err) {
            console.error('Billing error:', err);
            setError(err.response?.data?.detail || 'Failed to launch billing. Ensure Stripe is configured.');
        } finally {
            setIsBillingLoading(false);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSearchQuery('');
        setCurrentPage(1);
    };

    const getPaginatedData = (data, searchFields) => {
        let filtered = data;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = data.filter(item =>
                searchFields.some(field => String(item[field] || '').toLowerCase().includes(query))
            );
        }

        const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
        const validPage = Math.max(1, Math.min(currentPage, totalPages));
        if (validPage !== currentPage && filtered.length > 0) setCurrentPage(validPage);

        const currentData = filtered.slice((validPage - 1) * ITEMS_PER_PAGE, validPage * ITEMS_PER_PAGE);
        return { currentData, totalPages, totalItems: filtered.length };
    };

    const renderPagination = (totalPages) => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#3c3c3c]">
                <div className="flex items-center text-sm text-[#808080]">
                    Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-1 rounded-md text-gray-500 hover:bg-[#3c3c3c] disabled:opacity-50 transition-colors"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1 rounded-md text-gray-500 hover:bg-[#3c3c3c] disabled:opacity-50 transition-colors"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        );
    };

    // Calculate paginated data
    const { currentData: paginatedMembers, totalPages: memberPages, totalItems: totalFilteredMembers } = getPaginatedData(members, ['email', 'first_name', 'last_name', 'role']);
    const { currentData: paginatedInvites, totalPages: invitePages, totalItems: totalFilteredInvites } = getPaginatedData(invitations, ['email', 'role']);

    if (!currentOrganization) {
        return (
            <div className="flex items-center justify-center h-full p-8">
                <div className="text-center">
                    <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-300 mb-2">No Organization Selected</h2>
                    <p className="text-gray-400">Please select an organization to manage.</p>
                </div>
            </div>
        );
    }

    const TABS = [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
        { id: 'members', label: 'Members', icon: Users },
        // Only show sensitive tabs to admins/editors
        ...(currentOrganization.role === 'admin' || currentOrganization.role === 'editor' ? [
            { id: 'invitations', label: 'Invitations', icon: Mail },
            { id: 'billing', label: 'Plans & Upgrades', icon: CreditCard },
            { id: 'apikeys', label: 'API Keys', icon: Key },
        ] : []),
        ...(currentOrganization.role === 'admin' ? [
            { id: 'auditlogs', label: 'Audit Logs', icon: Activity },
            { id: 'data', label: 'Data & Compliance', icon: Download },
        ] : [])
    ];

    return (
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                    <p className="text-sm text-gray-400">Manage your organization, team, and billing</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8">
                {/* Sidebar Navigation */}
                <div className="w-full md:w-64 flex-shrink-0">
                    <div className="bg-[#252526] border-[#3c3c3c] rounded-xl overflow-hidden shadow-sm">
                        <div className="p-2 space-y-1">
                            {TABS.map(tab => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabChange(tab.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive
                                            ? 'bg-blue-900/20 text-blue-400 shadow-sm'
                                            : 'text-gray-400 hover:bg-[#323232] hover:text-gray-200'
                                            }`}
                                    >
                                        <Icon size={18} className={isActive ? 'text-blue-400' : 'text-gray-400'} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 min-w-0">
                    {/* Organization Info Card (Members/Invitations only) */}
                    {(activeTab === 'members' || activeTab === 'invitations') && (
                        <div className="bg-[#252526] border-[#3c3c3c] rounded-xl p-5 mb-6 shadow-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-xs text-[#808080] uppercase tracking-wide mb-1">Name</p>
                                    <p className="font-medium text-white truncate">{currentOrganization.name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#808080] uppercase tracking-wide mb-1" title="A URL-friendly version of your organization name">Slug ⓘ</p>
                                    <p className="font-mono text-sm text-[#cccccc] truncate">{currentOrganization.slug}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#808080] uppercase tracking-wide mb-1">Tier</p>
                                    <p className="font-medium text-white capitalize truncate">
                                        {currentOrganization.tier || 'Free'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#808080] uppercase tracking-wide mb-1">Your Role</p>
                                    <p className="font-medium text-white capitalize truncate">
                                        {currentOrganization.role}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Org Settings Card */}
                            <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl p-5 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Building2 size={18} className="text-[#808080]" />
                                        <h3 className="font-semibold text-white">Organization Settings</h3>
                                    </div>
                                    {currentOrganization.role === 'admin' && !isEditingOrg && (
                                        <button
                                            onClick={() => {
                                                setEditOrgName(currentOrganization.name || '');
                                                setEditOrgSlug(currentOrganization.slug || '');
                                                setOrgSaveError(null);
                                                setIsEditingOrg(true);
                                            }}
                                            className="flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                        >
                                            <Edit3 size={14} />
                                            Edit
                                        </button>
                                    )}
                                </div>

                                {isEditingOrg ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-[#808080] uppercase tracking-wide mb-1.5">Name</label>
                                                <input
                                                    type="text"
                                                    value={editOrgName}
                                                    onChange={e => setEditOrgName(e.target.value)}
                                                    className="w-full bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-[#808080] uppercase tracking-wide mb-1.5">Slug</label>
                                                <input
                                                    type="text"
                                                    value={editOrgSlug}
                                                    onChange={e => setEditOrgSlug(e.target.value)}
                                                    className="w-full bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg px-3 py-2 text-white text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                                />
                                            </div>
                                        </div>
                                        {orgSaveError && (
                                            <div className="flex items-center gap-2 text-red-400 text-sm">
                                                <AlertCircle size={14} />
                                                {orgSaveError}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={handleUpdateOrg}
                                                disabled={orgSaveLoading || !editOrgName.trim() || !editOrgSlug.trim()}
                                                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                                            >
                                                {orgSaveLoading ? (
                                                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                ) : (
                                                    <Save size={14} />
                                                )}
                                                Save
                                            </button>
                                            <button
                                                onClick={() => { setIsEditingOrg(false); setOrgSaveError(null); }}
                                                className="flex items-center gap-1.5 px-4 py-2 border border-[#555] text-gray-400 rounded-lg text-sm hover:bg-[#333] transition-colors"
                                            >
                                                <X size={14} />
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <p className="text-xs text-[#808080] uppercase tracking-wide mb-1">Name</p>
                                            <p className="font-medium text-white truncate">{currentOrganization.name}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-[#808080] uppercase tracking-wide mb-1" title="A URL-friendly version of your organization name">Slug ⓘ</p>
                                            <p className="font-mono text-sm text-[#cccccc] truncate">{currentOrganization.slug}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-[#808080] uppercase tracking-wide mb-1">Tier</p>
                                            <p className="font-medium text-white capitalize">{currentOrganization.tier || 'Free'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-[#808080] uppercase tracking-wide mb-1">Your Role</p>
                                            <p className="font-medium text-white capitalize">{currentOrganization.role}</p>
                                        </div>
                                    </div>
                                )}
                                {orgSaveSuccess && (
                                    <div className="mt-3 flex items-center gap-2 text-green-400 text-sm">
                                        <CheckCircle size={14} />
                                        Organization updated successfully.
                                    </div>
                                )}
                            </div>

                            <UsageMeters usage={usage} loading={usageLoading} />
                        </div>
                    )}

                    {/* Error/Success Messages */}
                    {error && (
                        <div className="flex items-center gap-3 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg mb-4">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-200">×</button>
                        </div>
                    )}
                    {inviteSuccess && (
                        <div className="flex items-center gap-3 bg-green-900/20 border border-green-800 text-green-400 px-4 py-3 rounded-lg mb-4">
                            <CheckCircle size={18} />
                            <span>{inviteSuccess}</span>
                            <button onClick={() => setInviteSuccess(null)} className="ml-auto text-green-500 hover:text-green-200">×</button>
                        </div>
                    )}

                    {/* Invitations Tab */}
                    {activeTab === 'invitations' && (
                        <div className="space-y-6">
                            {/* Create Invitation Form */}
                            <div className="bg-[#252526] border-[#3c3c3c] rounded-xl p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <UserPlus size={18} className="text-blue-500" />
                                    <h3 className="font-semibold text-white">Invite New Member</h3>
                                </div>
                                <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-4 items-end">
                                    <div className="flex-1 w-full">
                                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address</label>
                                        <input
                                            type="email"
                                            value={inviteEmail}
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            placeholder="alwynhawk@gmail.com"
                                            className="w-full bg-[#1e1e1e] border-[#3c3c3c] rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow"
                                            required
                                        />
                                    </div>
                                    <div className="w-full sm:w-auto">
                                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Role</label>
                                        <select
                                            value={inviteRole}
                                            onChange={(e) => setInviteRole(e.target.value)}
                                            className="w-full sm:w-auto bg-[#1e1e1e] border-[#3c3c3c] rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow cursor-pointer"
                                        >
                                            <option value="viewer">Viewer</option>
                                            <option value="editor">Editor</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={inviteLoading}
                                        className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {inviteLoading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Mail size={16} />
                                                Send Invite
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>

                            {/* Pending Invitations List */}
                            <div className="bg-[#252526] border-[#3c3c3c] rounded-xl shadow-sm overflow-hidden">
                                <div className="flex items-center gap-2 p-4 border-b border-[#3c3c3c]">
                                    <Clock size={18} className="text-[#808080]" />
                                    <h3 className="font-semibold text-white">Pending Invitations</h3>
                                    <span className="ml-auto text-sm text-[#808080]">{invitations.length} pending</span>
                                </div>
                                {loading ? (
                                    <div className="flex items-center justify-center p-8">
                                        <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                                    </div>
                                ) : invitations.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Mail className="w-12 h-12 mx-auto text-[#4c4c4c] mb-3" />
                                        <p className="text-[#808080]">No pending invitations</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-[#1e1e1e]">
                                                <tr>
                                                    <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Email</th>
                                                    <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Role</th>
                                                    <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Sent</th>
                                                    <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Expires</th>
                                                    <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#3c3c3c]">
                                                {paginatedInvites.map((invite) => (
                                                    <tr key={invite.id} className="hover:bg-[#2a2a2a] transition-colors">
                                                        <td className="p-4 text-white font-medium">{invite.email}</td>
                                                        <td className="p-4">
                                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(invite.role)}`}>
                                                                {invite.role}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-sm text-[#808080]">
                                                            {formatDate(invite.created_at)}
                                                        </td>
                                                        <td className="p-4">
                                                            <span className="text-sm text-orange-400 font-medium">
                                                                {getTimeRemaining(invite.expires_at)}
                                                            </span>
                                                        </td>
                                                        <td className="p-4">
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    onClick={() => handleResendInvitation(invite.id)}
                                                                    className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                                                                >
                                                                    <Mail size={14} />
                                                                    Resend
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRevoke(invite.id)}
                                                                    className="flex items-center gap-1.5 text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
                                                                >
                                                                    <Trash2 size={14} />
                                                                    Revoke
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {renderPagination(invitePages)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Members Tab */}
                    {activeTab === 'members' && (
                        <div className="bg-[#252526] border-[#3c3c3c] rounded-xl overflow-hidden shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border-b border-[#3c3c3c]">
                                <div className="flex items-center gap-2">
                                    <Users size={18} className="text-gray-400" />
                                    <h3 className="font-semibold text-white">Organization Members</h3>
                                    <span className="text-sm text-gray-400 ml-2">({totalFilteredMembers})</span>
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search size={14} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        placeholder="Search members..."
                                        className="pl-9 pr-4 py-2 w-full sm:w-64 bg-[#1e1e1e] border-[#3c3c3c] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-500 transition-all shadow-sm"
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center p-8">
                                    <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-[#1e1e1e]">
                                            <tr>
                                                <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">User</th>
                                                <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Role</th>
                                                <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Joined</th>
                                                <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[#3c3c3c]">
                                            {paginatedMembers.map((member) => {
                                                const isMe = user?.id === member.user_id;
                                                return (
                                                    <tr key={member.user_id} className="hover:bg-[#2a2a2a] transition-colors">
                                                        <td className="p-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                                                                    {member.first_name ? member.first_name[0] : (isMe ? 'Y' : 'U')}
                                                                </div>
                                                                <div>
                                                                    <div className="font-medium text-white flex items-center gap-2">
                                                                        {isMe ? 'You' : (member.first_name ? `${member.first_name} ${member.last_name || ''}` : `User ${member.user_id.substring(0, 8)}...`)}
                                                                        {isMe && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-800 text-yellow-200">Me</span>}
                                                                    </div>
                                                                    <div className="text-xs text-[#808080]">
                                                                        {member.email || 'No email available'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(member.role)}`}>
                                                                {member.role}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-sm text-gray-400">
                                                            {member.joined_at ? formatDate(member.joined_at) : 'Unknown'}
                                                        </td>
                                                        <td className="p-4">
                                                            {!isMe && currentOrganization.role === 'admin' && (
                                                                <div className="flex items-center gap-3">
                                                                    <select
                                                                        value={member.role}
                                                                        onChange={e => handleRoleChange(member.user_id, e.target.value)}
                                                                        className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                                                    >
                                                                        <option value="viewer">Viewer</option>
                                                                        <option value="editor">Editor</option>
                                                                        <option value="admin">Admin</option>
                                                                    </select>
                                                                    <button
                                                                        onClick={() => handleRemoveMember(member.user_id)}
                                                                        className="flex items-center gap-1.5 text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
                                                                        title="Remove from organization"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {paginatedMembers.length === 0 && (
                                        <div className="p-8 text-center text-gray-400">
                                            No members found matching your search.
                                        </div>
                                    )}
                                    {paginatedMembers.length > 0 && renderPagination(memberPages)}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Billing & Usage */}
                    {activeTab === 'billing' && (
                        <div className="space-y-6">
                            {/* Header */}
                            <div>
                                <h3 className="text-lg font-semibold text-white">Plans & Usage</h3>
                                <p className="text-sm text-gray-400 mt-1">
                                    Pick the plan that fits your team. Upgrade or downgrade anytime, changes apply at the next billing cycle.
                                </p>
                            </div>

                            {/* Tier Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                {TIERS.map((tier) => {
                                    const currentTier = currentOrganization?.tier || 'free';
                                    const isCurrent = currentTier === tier.id;
                                    const tierOrder = { free: 0, starter: 1, team: 2, pro: 3 };
                                    const isDowngrade = tierOrder[tier.id] < tierOrder[currentTier];
                                    const isUpgrade = tierOrder[tier.id] > tierOrder[currentTier];

                                    return (
                                        <div
                                            key={tier.id}
                                            className={`relative rounded-xl border p-6 flex flex-col transition-all ${isCurrent
                                                ? 'border-blue-500 bg-blue-900/10 ring-1 ring-blue-500/30'
                                                : 'border-[#3c3c3c] bg-[#252526]'
                                                }`}
                                        >
                                            {isCurrent && (
                                                <span className="absolute -top-2.5 left-4 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider bg-blue-600 text-white rounded-full shadow-sm">
                                                    Current Plan
                                                </span>
                                            )}

                                            {/* Tier name & price */}
                                            <div className="mb-5 pt-1">
                                                <h4 className="text-base font-bold text-white">{tier.name}</h4>
                                                <div className="mt-2 flex items-baseline gap-1">
                                                    {tier.price === 0 ? (
                                                        <span className="text-3xl font-extrabold text-white">Free</span>
                                                    ) : tier.price !== null ? (
                                                        <>
                                                            <span className="text-3xl font-extrabold text-white">${tier.price}</span>
                                                            <span className="text-sm text-gray-400">/mo</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-xl font-bold text-white">Custom</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-[#606060] mt-1">{tier.metered}</p>
                                            </div>

                                            {/* Feature list */}
                                            <ul className="space-y-2.5 mb-6 flex-1">
                                                {tier.features.map((f, i) => (
                                                    <li key={i} className="text-sm text-gray-400">
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>

                                            {/* Action button */}
                                            {isCurrent && tier.id === 'free' ? (
                                                <button disabled className="w-full py-2 px-4 rounded-lg text-sm font-medium border border-[#444] text-[#606060] cursor-default">
                                                    Current Plan
                                                </button>
                                            ) : isCurrent ? (
                                                <button
                                                    onClick={() => handleSelectTier(tier)}
                                                    disabled={isBillingLoading}
                                                    className="w-full py-2 px-4 rounded-lg text-sm font-medium border border-[#555] text-gray-300 hover:bg-[#333] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                    Manage Billing
                                                </button>
                                            ) : isUpgrade ? (
                                                <button
                                                    onClick={() => handleSelectTier(tier)}
                                                    disabled={isBillingLoading}
                                                    className="w-full py-2 px-4 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                                >
                                                    {isBillingLoading && <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />}
                                                    Upgrade
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleSelectTier(tier)}
                                                    disabled={isBillingLoading}
                                                    className="w-full py-2 px-4 rounded-lg text-sm font-medium border border-[#555] text-gray-400 hover:bg-[#333] transition-colors disabled:opacity-50"
                                                >
                                                    Downgrade
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="text-center space-y-1">
                                {subscriptionInfo?.renews_at && (
                                    <p className="text-xs text-[#808080]">
                                        {subscriptionInfo.cancel_at_period_end
                                            ? <>Subscription cancelled. Access continues until <span className="text-white">{new Date(subscriptionInfo.renews_at * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>.</>
                                            : <>Subscription renews on <span className="text-white">{new Date(subscriptionInfo.renews_at * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>.</>
                                        }
                                    </p>
                                )}
                                <p className="text-xs text-[#606060]">
                                    Access continues until end of billing period. Payments handled securely by Stripe.
                                </p>
                            </div>

                            {/* Invoice History */}
                            {currentOrganization?.stripe_customer_id && (
                                <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl overflow-hidden shadow-sm mt-6">
                                    <div className="flex items-center gap-2 p-4 border-b border-[#3c3c3c]">
                                        <CreditCard size={16} className="text-gray-400" />
                                        <h4 className="font-semibold text-white text-sm">Invoice History</h4>
                                    </div>
                                    {isInvoicesLoading ? (
                                        <div className="p-6 text-center text-gray-500 text-sm">Loading invoices...</div>
                                    ) : invoices.length === 0 ? (
                                        <div className="p-6 text-center text-gray-500 text-sm">No invoices found.</div>
                                    ) : (
                                        <table className="w-full text-sm">
                                            <thead className="bg-[#1e1e1e]">
                                                <tr>
                                                    <th className="text-left p-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Invoice</th>
                                                    <th className="text-left p-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Period</th>
                                                    <th className="text-left p-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Amount</th>
                                                    <th className="text-left p-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Status</th>
                                                    <th className="text-left p-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">PDF</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#3c3c3c]">
                                                {invoices.map(inv => (
                                                    <tr key={inv.id} className="hover:bg-[#2a2a2a] transition-colors">
                                                        <td className="p-3 text-gray-300">{inv.number || inv.id.slice(-8)}</td>
                                                        <td className="p-3 text-gray-400 text-xs">
                                                            {new Date(inv.period_start * 1000).toLocaleDateString()} – {new Date(inv.period_end * 1000).toLocaleDateString()}
                                                        </td>
                                                        <td className="p-3 text-white font-medium">
                                                            {(inv.amount_paid / 100).toLocaleString(undefined, { style: 'currency', currency: inv.currency.toUpperCase() })}
                                                        </td>
                                                        <td className="p-3">
                                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${inv.status === 'paid' ? 'bg-green-900/30 text-green-400' : inv.status === 'open' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-red-900/30 text-red-400'}`}>
                                                                {inv.status}
                                                            </span>
                                                        </td>
                                                        <td className="p-3">
                                                            {inv.invoice_pdf ? (
                                                                <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 text-xs">
                                                                    <Download size={12} /> PDF
                                                                </a>
                                                            ) : inv.hosted_invoice_url ? (
                                                                <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 text-xs">
                                                                    <ExternalLink size={12} /> View
                                                                </a>
                                                            ) : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* API Keys */}
                    {activeTab === 'apikeys' && (
                        <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-white">API Keys</h3>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Generate keys for partner access to the Contract Version API.
                                    </p>
                                </div>
                                <button
                                    onClick={() => { setShowCreateKey(true); setNewKeyName(''); setCreatedKey(null); }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm inline-flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create Key
                                </button>
                            </div>

                            {/* Created Key Modal */}
                            {showCreateKey && (
                                <div className="bg-[#252526] border-[#3c3c3c] rounded-xl p-5 shadow-sm">
                                    {!createdKey ? (
                                        <div className="space-y-4">
                                            <div className="flex items-end gap-3">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-300 mb-1">Key Name</label>
                                                    <input
                                                        type="text"
                                                        value={newKeyName}
                                                        onChange={(e) => setNewKeyName(e.target.value)}
                                                        placeholder="e.g. Partner Integration"
                                                        className="w-full px-3 py-2 rounded-lg border border-[#555] bg-[#1e1e1e] text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                                    />
                                                </div>
                                                <button
                                                    onClick={async () => {
                                                        if (!newKeyName.trim()) return;
                                                        try {
                                                            const key = await createApiKey(newKeyName.trim(), newKeyScopes);
                                                            setCreatedKey(key);
                                                            setNewKeyScopes([]);
                                                            const keys = await getApiKeys();
                                                            setApiKeys(keys);
                                                        } catch (err) {
                                                            setError(err.response?.data?.detail || 'Failed to create API key');
                                                        }
                                                    }}
                                                    disabled={!newKeyName.trim()}
                                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50"
                                                >
                                                    Generate
                                                </button>
                                                <button
                                                    onClick={() => { setShowCreateKey(false); setNewKeyScopes([]); }}
                                                    className="px-4 py-2 border border-[#555] text-gray-400 rounded-lg hover:bg-[#333] transition-colors text-sm"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    Scopes <span className="text-gray-500 font-normal">(leave empty for full access)</span>
                                                </label>
                                                <div className="flex flex-wrap gap-3">
                                                    {['contracts:read', 'versions:read', 'openapi:read'].map(scope => (
                                                        <label key={scope} className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                                                            <input
                                                                type="checkbox"
                                                                checked={newKeyScopes.includes(scope)}
                                                                onChange={(e) => {
                                                                    setNewKeyScopes(prev =>
                                                                        e.target.checked ? [...prev, scope] : prev.filter(s => s !== scope)
                                                                    );
                                                                }}
                                                                className="rounded border-gray-600 bg-[#1e1e1e] text-blue-500"
                                                            />
                                                            {scope}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                                <span className="text-sm font-medium text-white">API key generated successfully. Please copy it now, as it will not be displayed again.</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 bg-[#1e1e1e] border-[#3c3c3c] rounded-lg px-4 py-3 font-mono text-sm text-gray-100 break-all">
                                                    {showRawKey ? createdKey.raw_key : '•'.repeat(createdKey.raw_key.length)}
                                                </div>
                                                <button
                                                    onClick={() => setShowRawKey(!showRawKey)}
                                                    className="p-2.5 rounded-lg border border-[#555] text-gray-300 hover:bg-[#333] transition-colors"
                                                    title={showRawKey ? 'Hide key' : 'Show key'}
                                                >
                                                    {showRawKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(createdKey.raw_key);
                                                        setCopiedKey(true);
                                                        setTimeout(() => setCopiedKey(false), 2000);
                                                    }}
                                                    className={`p-2.5 rounded-lg border transition-colors ${copiedKey
                                                        ? 'border-green-400 text-green-500 bg-green-900/20'
                                                        : 'border-[#555] text-gray-300 hover:bg-[#333]'
                                                        }`}
                                                    title={copiedKey ? 'Copied!' : 'Copy to clipboard'}
                                                >
                                                    {copiedKey ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => { setShowCreateKey(false); setCreatedKey(null); setShowRawKey(false); }}
                                                className="mt-3 text-sm text-[#808080] group-hover:text-[#909090] transition-colors"
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Keys Table */}
                            {apiKeys.length > 0 ? (
                                <div className="bg-[#252526] border-[#3c3c3c] rounded-xl shadow-sm overflow-hidden">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-[#3c3c3c]">
                                                <th className="text-left px-5 py-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Name</th>
                                                <th className="text-left px-5 py-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Key</th>
                                                <th className="text-left px-5 py-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Created</th>
                                                <th className="text-left px-5 py-3 text-xs font-semibold text-[#808080] uppercase tracking-wide">Last Used</th>
                                                <th className="text-right px-5 py-3 text-xs font-semibold text-[#808080] uppercase tracking-wide"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {apiKeys.map((k) => (
                                                <tr key={k.id} className="border-b border-[#333] last:border-0">
                                                    <td className="px-5 py-3 text-sm font-medium text-white">{k.name}</td>
                                                    <td className="px-5 py-3 text-sm font-mono text-gray-400">{k.key_prefix}</td>
                                                    <td className="px-5 py-3 text-sm text-gray-400">
                                                        {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}
                                                    </td>
                                                    <td className="px-5 py-3 text-sm text-gray-400">
                                                        {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never'}
                                                    </td>
                                                    <td className="px-5 py-3 text-right">
                                                        {revokeConfirmId === k.id ? (
                                                            <div className="flex items-center justify-end gap-2">
                                                                <span className="text-xs text-gray-400">Revoke?</span>
                                                                <button
                                                                    onClick={async () => {
                                                                        try {
                                                                            await revokeApiKey(k.id);
                                                                            setApiKeys(prev => prev.filter(key => key.id !== k.id));
                                                                            setRevokeConfirmId(null);
                                                                        } catch (err) {
                                                                            setError(err.response?.data?.detail || 'Failed to revoke key');
                                                                        }
                                                                    }}
                                                                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors font-medium"
                                                                >
                                                                    Yes
                                                                </button>
                                                                <button
                                                                    onClick={() => setRevokeConfirmId(null)}
                                                                    className="px-2 py-1 text-xs border border-[#555] text-gray-400 rounded hover:bg-[#333] transition-colors"
                                                                >
                                                                    No
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setRevokeConfirmId(k.id)}
                                                                className="text-xs text-red-500 hover:text-red-400 transition-colors font-medium"
                                                            >
                                                                Revoke
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : !isApiKeysLoading && (
                                <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl p-8 shadow-sm text-center">
                                    <Key className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                                    <p className="text-sm text-gray-400">
                                        No API keys yet. Create one to give partners access to your contracts.
                                    </p>
                                </div>
                            )}

                            {/* Quick Start Guide */}
                            <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-xl p-5">
                                <p className="text-sm font-semibold text-gray-200 mb-3">Quick Start</p>
                                <p className="text-xs text-gray-400 mb-4">
                                    Share your API key with partners so they can programmatically fetch your contract schemas. Include the key in the <code className="bg-[#333] px-1 rounded">Authorization</code> header of each request.
                                </p>
                                <div className="space-y-2">
                                    <div className="flex items-start gap-3">
                                        <div>
                                            <code className="text-xs font-mono text-[#cccccc]">/api/v1/contracts</code>
                                            <p className="text-xs text-gray-500 mt-0.5">List all contracts</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-xs font-mono text-blue-400 whitespace-nowrap mt-0.5">GET</span>
                                        <div>
                                            <code className="text-xs font-mono text-[#cccccc]">/api/v1/contracts/:id</code>
                                            <p className="text-xs text-gray-500 mt-0.5">Get a contract with its full schema</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-xs font-mono text-blue-400 whitespace-nowrap mt-0.5">GET</span>
                                        <div>
                                            <code className="text-xs font-mono text-[#cccccc]">/api/v1/contracts/:id/versions</code>
                                            <p className="text-xs text-gray-500 mt-0.5">List version history for a contract</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-xs font-mono text-blue-400 whitespace-nowrap mt-0.5">GET</span>
                                        <div>
                                            <code className="text-xs font-mono text-gray-300">/api/v1/contracts/:id/versions/:v</code>
                                            <p className="text-xs text-gray-500 mt-0.5">Get a specific version with its schema</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* Audit Logs */}
                    {activeTab === 'auditlogs' && currentOrganization.role === 'admin' && (
                        <div className="space-y-6">
                            <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl shadow-sm overflow-hidden">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border-b border-[#3c3c3c]">
                                    <div className="flex items-center gap-2">
                                        <Activity size={18} className="text-[#808080]" />
                                        <h3 className="font-semibold text-white">Audit Logs</h3>
                                        <span className="text-sm text-gray-400 ml-2">({auditLogs.length})</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Search size={14} className="text-gray-500" />
                                            </div>
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Search logs..."
                                                className="w-full sm:w-64 pl-9 pr-3 py-1.5 bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                            />
                                            {searchQuery && (
                                                <button
                                                    onClick={() => setSearchQuery('')}
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300"
                                                >
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => {
                                                const csvContent = "data:text/csv;charset=utf-8,"
                                                    + "Action,Resource Type,Resource ID,User,Status,Time\n"
                                                    + auditLogs.map(log =>
                                                        `${log.action_type},${log.resource_type || ''},${log.resource_id || ''},${log.created_by || 'System'},${log.status},${new Date(log.created_at).toISOString()}`
                                                    ).join("\n");
                                                const encodedUri = encodeURI(csvContent);
                                                const link = document.createElement("a");
                                                link.setAttribute("href", encodedUri);
                                                link.setAttribute("download", `driftops_audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }}
                                            className="px-3 py-1.5 bg-[#3c3c3c] hover:bg-[#4c4c4c] border border-[#555] rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
                                            title="Export to CSV"
                                        >
                                            <Download size={14} />
                                            <span className="hidden sm:inline">Export</span>
                                        </button>
                                    </div>
                                </div>
                                {isAuditLogsLoading ? (
                                    <div className="flex items-center justify-center p-8">
                                        <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                                    </div>
                                ) : auditLogs.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Clock className="w-12 h-12 mx-auto text-[#4c4c4c] mb-3" />
                                        <p className="text-[#808080]">No activity recorded yet.</p>
                                    </div>
                                ) : (() => {
                                    // Local pagination for audit logs
                                    let filteredLogs = auditLogs;
                                    if (searchQuery) {
                                        const query = searchQuery.toLowerCase();
                                        filteredLogs = auditLogs.filter(log =>
                                            log.action_type.toLowerCase().includes(query) ||
                                            (log.resource_type && log.resource_type.toLowerCase().includes(query)) ||
                                            (log.created_by && log.created_by.toLowerCase().includes(query)) ||
                                            log.status.toLowerCase().includes(query)
                                        );
                                    }
                                    const logPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE) || 1;
                                    const currentLogData = filteredLogs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

                                    return (
                                        <>
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead className="bg-[#1e1e1e]">
                                                        <tr>
                                                            <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Action</th>
                                                            <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">User</th>
                                                            <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Time</th>
                                                            <th className="text-left p-4 text-xs font-semibold text-[#808080] uppercase tracking-wide">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-[#3c3c3c]">
                                                        {currentLogData.length > 0 ? currentLogData.map((log) => (
                                                            <tr key={log.id} className="hover:bg-[#2a2a2a] transition-colors">
                                                                <td className="p-4 text-sm text-white">
                                                                    <div className="font-medium capitalize">{log.action_type.replace(/_/g, ' ')}</div>
                                                                    {log.resource_type && (
                                                                        <div className="text-xs text-[#808080] mt-0.5 capitalize">{log.resource_type} {log.resource_id ? `#${log.resource_id}` : ''}</div>
                                                                    )}
                                                                </td>
                                                                <td className="p-4 flex items-center gap-2">
                                                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-[10px] font-bold">
                                                                        {log.created_by ? log.created_by[0].toUpperCase() : 'S'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-300 truncate w-32">{log.created_by || 'System'}</div>
                                                                </td>
                                                                <td className="p-4 text-sm text-[#808080]">
                                                                    {formatDate(log.created_at)}
                                                                </td>
                                                                <td className="p-4">
                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${log.status === 'success' ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'}`}>
                                                                        {log.status !== 'success' && <AlertCircle size={10} className="mr-1 inline" />}
                                                                        {log.status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )) : (
                                                            <tr>
                                                                <td colSpan="4" className="p-8 text-center text-[#808080]">
                                                                    <Search className="w-8 h-8 mx-auto text-[#4c4c4c] mb-3" />
                                                                    No logs match your search.
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {renderPagination(logPages)}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Data & Compliance Tab */}
                    {activeTab === 'data' && (
                        <div className="bg-[#252526] border border-[#3c3c3c] rounded-xl overflow-hidden">
                            <div className="p-5 border-b border-[#3c3c3c]">
                                <h2 className="text-base font-semibold text-white">Data &amp; Compliance</h2>
                                <p className="text-sm text-[#808080] mt-1">
                                    Export and manage your organization's data for backup, migration, or compliance audits.
                                </p>
                            </div>

                            <div className="p-5 space-y-6">
                                {/* Backup */}
                                <div>
                                    <h3 className="text-sm font-semibold text-white mb-1">Full Organization Backup</h3>
                                    <p className="text-sm text-[#808080] mb-2">
                                        Download a complete JSON snapshot of your organization including Data Contracts,
                                        Webhook Monitors, Cloud Connections, Pipelines, and Members.
                                    </p>
                                    <p className="text-xs text-[#606060] mb-4">
                                        Cloud connection credentials are excluded from this export. You will need to re-enter
                                        them if restoring to a new organization.
                                    </p>
                                    <div className="flex items-center gap-4">
                                        <button
                                            id="admin-download-backup-btn"
                                            onClick={async () => {
                                                setIsBackupLoading(true);
                                                setBackupSuccess(false);
                                                setBackupError(null);
                                                try {
                                                    await downloadOrganizationBackup();
                                                    setBackupSuccess(true);
                                                    setTimeout(() => setBackupSuccess(false), 4000);
                                                } catch (err) {
                                                    setBackupError(err.response?.data?.detail || 'Export failed. Please try again.');
                                                } finally {
                                                    setIsBackupLoading(false);
                                                }
                                            }}
                                            disabled={isBackupLoading}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:cursor-wait text-white text-sm font-medium rounded-lg transition-colors"
                                        >
                                            {isBackupLoading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    Preparing export...
                                                </>
                                            ) : (
                                                'Export Organization Data'
                                            )}
                                        </button>
                                        {backupSuccess && (
                                            <span className="text-green-400 text-sm">Download started!</span>
                                        )}
                                        {backupError && (
                                            <span className="text-red-400 text-sm">{backupError}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="border-t border-[#3c3c3c]" />

                                {/* Import */}
                                <div>
                                    <h3 className="text-sm font-semibold text-white mb-1">Import from Backup</h3>
                                    <p className="text-sm text-[#808080] mb-4">
                                        Restore data from a previously exported DriftOps backup file. Contracts and Monitors are
                                        imported; cloud credentials, pipelines, and members must be reconfigured manually.
                                    </p>

                                    <div
                                        id="import-backup-dropzone"
                                        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                                        onDragLeave={() => setIsDragOver(false)}
                                        onDrop={e => {
                                            e.preventDefault();
                                            setIsDragOver(false);
                                            setImportResult(null);
                                            setImportError(null);
                                            const dropped = e.dataTransfer.files[0];
                                            if (dropped && dropped.name.endsWith('.json')) {
                                                setImportFile(dropped);
                                            } else {
                                                setImportError('Please drop a .json backup file.');
                                            }
                                        }}
                                        onClick={() => document.getElementById('import-backup-input').click()}
                                        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors select-none ${isDragOver
                                            ? 'border-blue-500 bg-blue-900/10'
                                            : importFile
                                                ? 'border-[#4c4c4c] bg-[#1e1e1e]'
                                                : 'border-[#3c3c3c] hover:border-[#555] hover:bg-[#1e1e1e]'
                                            }`}
                                    >
                                        <input
                                            id="import-backup-input"
                                            type="file"
                                            accept=".json,application/json"
                                            className="hidden"
                                            onChange={e => {
                                                setImportResult(null);
                                                setImportError(null);
                                                setImportFile(e.target.files[0] || null);
                                                e.target.value = '';
                                            }}
                                        />
                                        {importFile ? (
                                            <div className="text-center">
                                                <p className="text-white text-sm font-medium">{importFile.name}</p>
                                                <p className="text-[#606060] text-xs mt-0.5">
                                                    {(importFile.size / 1024).toFixed(1)} KB, click to change
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <p className="text-[#808080] text-sm">
                                                    Drop your backup file here, or{' '}
                                                    <span className="text-blue-400">click to browse</span>
                                                </p>
                                                <p className="text-[#505050] text-xs mt-0.5">.json files only</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4 flex items-center gap-4">
                                        <button
                                            id="admin-import-backup-btn"
                                            disabled={!importFile || isImporting}
                                            onClick={async () => {
                                                setIsImporting(true);
                                                setImportError(null);
                                                setImportResult(null);
                                                try {
                                                    const result = await importOrganizationBackup(importFile);
                                                    setImportResult(result);
                                                    setImportFile(null);
                                                } catch (err) {
                                                    setImportError(err.response?.data?.detail || 'Import failed. Is this a valid DriftOps backup?');
                                                } finally {
                                                    setIsImporting(false);
                                                }
                                            }}
                                            className="flex items-center gap-2 px-4 py-2 bg-[#1e2e1e] hover:bg-[#273727] disabled:opacity-40 disabled:cursor-not-allowed text-green-400 text-sm font-medium rounded-lg border border-green-900/60 transition-colors"
                                        >
                                            {isImporting ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                                                    Importing...
                                                </>
                                            ) : (
                                                'Import Backup'
                                            )}
                                        </button>
                                        {importError && (
                                            <span className="text-red-400 text-sm">{importError}</span>
                                        )}
                                    </div>

                                    {importResult && (
                                        <div className="mt-5 border border-[#3c3c3c] rounded-lg overflow-hidden">
                                            <div className="px-4 py-3 border-b border-[#3c3c3c]">
                                                <span className="text-sm font-semibold text-white">Import complete</span>
                                                {importResult.source_org && (
                                                    <span className="text-xs text-[#606060] ml-2">
                                                        from <span className="text-gray-400">{importResult.source_org}</span>
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-4 grid grid-cols-2 gap-2 text-sm">
                                                {[
                                                    { label: 'Contracts imported', value: importResult.summary?.contracts_imported ?? 0, positive: true },
                                                    { label: 'Contracts skipped', value: importResult.summary?.contracts_skipped ?? 0, positive: false },
                                                    { label: 'Monitors imported', value: importResult.summary?.monitors_imported ?? 0, positive: true },
                                                    { label: 'Monitors skipped', value: importResult.summary?.monitors_skipped ?? 0, positive: false },
                                                ].map(row => (
                                                    <div key={row.label} className="flex items-center justify-between bg-[#1e1e1e] rounded-lg px-3 py-2">
                                                        <span className="text-[#808080]">{row.label}</span>
                                                        <span className={`font-bold tabular-nums ${row.positive && row.value > 0 ? 'text-green-400'
                                                            : row.value > 0 ? 'text-yellow-400'
                                                                : 'text-[#555]'
                                                            }`}>
                                                            {row.value}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </div >
            </div >
        </div >
    );
}
